import { ComponentPropsWithoutRef } from 'react';
import styled, { css, keyframes } from 'styled-components';
import { isMobile } from 'utils';
import { DEVICE } from 'constants/app/breakpoints';
import { TSizeType } from 'interfaces';

export type ButtonBaseProps = ComponentPropsWithoutRef<'button'> & {
  size: Omit<TSizeType, 'huge' | 'medium'>;
};

const OpacityIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;

const OpacityOut = keyframes`
  from {
    opacity: 1;
  }
  to {
    opacity: 0;
  }
`;

const ScaleIn = keyframes`
  from {
    transform: scale(0.01);
  }
  to {
    transform: scale(1);
  }
`;

const ScaleOut = keyframes`
  from {
    transform: scale(1);
  }
  to {
    transform: scale(0.01);
  }
`;

export const ModalOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  z-index: 1;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  opacity: 0;
`;

export const ModalDialog = styled.div`
  background-clip: padding-box;
  margin: 0 auto;
  overflow-x: hidden;
  overflow-y: scroll;
  ${isMobile()
    ? css`
        max-height: 100%;
      `
    : css`
        max-height: 90%;
      `}
  z-index: 2;
  @media screen and ${DEVICE.xl} {
    height: 100%;
    width: 100%;
  }
  &::-webkit-scrollbar {
    display: none;
  }
`;

export const Modal = styled.div<{ $fullwidth?: boolean }>`
  position: fixed;
  z-index: 1000;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  width: 100%;
  height: 100%;
  overflow-y: auto;
  display: flex;
  align-items: center;
  justify-content: center;
  @media screen and ${DEVICE.xl} {
    padding: 0;
  }
  &.open {
    ${ModalOverlay} {
      animation: ${OpacityIn} 0.2s ease-in-out forwards;
    }
    ${ModalDialog} {
      animation: ${OpacityIn} 0.2s ease-in-out forwards,
        ${ScaleIn} 0.2s ease-in-out forwards;
    }
  }
  &.close {
    ${ModalOverlay} {
      animation: ${OpacityOut} 0.2s ease-in-out forwards;
    }
    ${ModalDialog} {
      animation: ${OpacityOut} 0.2s ease-in-out forwards,
        ${ScaleOut} 0.2s ease-in-out forwards;
    }
  }
  ${props =>
    props.$fullwidth &&
    css`
      & {
        display: flex;
        align-items: center;
        justify-content: center;
        padding: unset;

        ${ModalDialog} {
          width: 80vw;
          height: 90vh;
          display: block;

          ${isMobile() &&
          css`
            width: 100%;
            height: 100%;
          `}
        }
        ${ModalBody} {
          width: 100%;
          height: 100%;
        }
      }
    `}
`;

export const ModalBody = styled.div<{ $bodyTransparent?: boolean }>`
  position: relative;
  display: flex;
  justify-content: center;
  width: fit-content;
  height: fit-content;
  word-wrap: break-word;
  border-radius: 16px;
  background-color: ${props =>
    props.$bodyTransparent ? 'transparent' : 'var(--v3-black-0)'};
  @media screen and ${DEVICE.xl} {
    height: 100%;
    width: 100%;
    border-radius: 0;
    max-height: 100%;
  }
`;

export const CloseButton = styled.button<ButtonBaseProps>`
  position: absolute;
  background: var(--v3-black-1);
  color: var(--v3-text-color);
  border-radius: 50%;
  top: 24px;
  right: 24px;
  width: 44px;
  height: 44px;
  font-size: 28px;
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 51;
  border: none;
  &:hover {
    cursor: pointer;
  }

  ${props =>
    props.size === 'small' &&
    css`
      top: 20px;
      right: 20px;
      width: 32px;
      height: 32px;
      font-size: 24px;
    `}
`;
